import { useAppDisplayModeContext } from '@archipro-design/aria';
import {
    useLoaderData,
    useNavigation,
    useSearchParams,
} from '@remix-run/react';
import type { loader } from '~/routes/_app.project.$urlSegment._index';

import AboutProfessional from '@modules/project/component/about-professional/AboutProfessional';
import { DetailPageButtons } from '@modules/business-manager/component/detail-page-buttons/DetailPageButtons';

import { DetailsPageRelatedTileGrid } from '~/ui-rocco/pages/details-page/DetailsPageRelatedTileGrid';

export const SHOW_ALL_PARAM_FEATURED_PRODUCTS = 'featured-products-show-all';
export const SHOW_ALL_PARAM_FEATURED_PROFESSIONALS =
    'featured-professionals-show-all';

const ProjectPageContent = () => {
    const { desktop } = useAppDisplayModeContext();
    const {
        project,
        featuredProducts,
        featuredProfessionals,
        featuredProjects,
        detailButton,
    } = useLoaderData<typeof loader>();

    const navigation = useNavigation();
    const [searchParams] = useSearchParams();

    const nextSearchParams = new URLSearchParams(navigation.location?.search);
    const showingAllFeaturedProducts = !!searchParams.get(
        SHOW_ALL_PARAM_FEATURED_PRODUCTS
    );
    const showingAllFeaturedProfessionals = !!searchParams.get(
        SHOW_ALL_PARAM_FEATURED_PROFESSIONALS
    );
    const featuredShowAllFeaturedProducts =
        nextSearchParams.get(SHOW_ALL_PARAM_FEATURED_PRODUCTS) === '1';
    const featuredShowAllFeaturedProfessionals =
        nextSearchParams.get(SHOW_ALL_PARAM_FEATURED_PROFESSIONALS) === '1';

    const isLoadingFeaturedProducts =
        navigation.state === 'loading' && featuredShowAllFeaturedProducts;
    const isLoadingFeaturedProfessionals =
        navigation.state === 'loading' && featuredShowAllFeaturedProfessionals;

    return (
        <div className="flex flex-col gap-y-12 px-4 md:px-0 md:gap-y-32">
            {/* Featured Products */}
            <DetailsPageRelatedTileGrid
                type="products"
                title={`Products used in ${project.Title}`}
                tiles={
                    featuredProducts.FeaturedProducts.products.map(
                        (product) => {
                            return {
                                model: product,
                                aspectRatio: 'square',
                                section: 'ProjectUsedProducts',
                            };
                        }
                    ) ?? []
                }
                cta={
                    showingAllFeaturedProducts ||
                    featuredProducts.FeaturedProducts.pageInfo.totalCount <=
                        (desktop ? 10 : 4)
                        ? undefined
                        : {
                              type: 'link',
                              to: `?${SHOW_ALL_PARAM_FEATURED_PRODUCTS}=1`,
                              label: 'View all',
                              loading: isLoadingFeaturedProducts,
                              preventScrollReset: true,
                          }
                }
                displayCount={{
                    desktop: 10,
                    mobile: 4,
                }}
                appDisplayMode={desktop ? 'desktop' : 'mobile'}
            />

            {/* Featured Professionals */}

            <DetailsPageRelatedTileGrid
                type="professionals"
                title={`Professionals used in ${project.Title}`}
                tiles={featuredProfessionals.FeaturedProfessionals.professionals.map(
                    (professional) => ({
                        model: professional,
                        aspectRatio: 'default',
                        section: 'ProjectUsedProfessionals',
                    })
                )}
                appDisplayMode={desktop ? 'desktop' : 'mobile'}
                cta={
                    showingAllFeaturedProfessionals ||
                    featuredProfessionals.FeaturedProfessionals.pageInfo
                        .totalCount <= 4
                        ? undefined
                        : {
                              type: 'link',
                              to: `?${SHOW_ALL_PARAM_FEATURED_PROFESSIONALS}=1`,
                              label: 'View all',
                              loading: isLoadingFeaturedProfessionals,
                              preventScrollReset: true,
                          }
                }
                displayCount={{
                    desktop: 4,
                    mobile: 4,
                }}
                noSlice={showingAllFeaturedProfessionals}
            />

            {/* Featured Projects */}

            <DetailsPageRelatedTileGrid
                type="projects"
                title={`More projects by ${project.Professional.Title}`}
                tiles={featuredProjects.FeaturedProjects.projects.map(
                    (project) => ({
                        model: project,
                        aspectRatio: 'default',
                        section: 'ProjectRelated',
                    })
                )}
                appDisplayMode={desktop ? 'desktop' : 'mobile'}
                cta={
                    featuredProjects.FeaturedProjects.pageInfo.totalCount <= 4
                        ? undefined
                        : {
                              type: 'link',
                              to: `${project.Professional.Link}/case-studies`,
                              label: 'View more',
                          }
                }
                displayCount={{
                    desktop: 4,
                    mobile: 4,
                }}
            />

            {desktop && (
                <>
                    <AboutProfessional data={project} />
                    <DetailPageButtons {...detailButton} />
                </>
            )}
        </div>
    );
};
export default ProjectPageContent;
